import { useEffect, useRef, useState } from 'react';
import EnsembleCard from './EnsembleCard';

export default function EnsembleSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const ensembles = [
    {
      id: 'ni',
      name: 'Ni [WaLai 和籟]',
      description: 'Timeless orchestration fusing gagaku and drums (6-15 members)',
      members: 15,
      image: 'https://public.readdy.ai/ai/img_res/2d5730540ba37577bda26abec403cd83.jpg',
      details: 'The largest orchestration fusing the timeless resonance of gagaku with the power of drums. A grand musical experience transcending time and space.',
      videoUrl: 'https://youtu.be/5Hlt9AqBq1g?si=UdW836WIVgardIA0',
    },
    {
      id: 'ha',
      name: 'Ha [EarthVoice + LaiOn]',
      description: 'Maximizing the primordial resonance of the island nation (4-7 members)',
      members: 7,
      image: 'https://readdy.ai/api/search-image?query=harmonious-blend-of-earth-and-sky-elements-representing-japanese-island-origins-layered-natural-textures-of-ocean-mountains-and-clouds-deep-blue-and-earth-tone-palette-serene-yet-powerful-atmosphere-minimalist-artistic-composition-emphasizing-unity-and-balance&width=600&height=800&seq=ensemble-ha-003&orientation=portrait',
      details: 'Through the fusion of EarthVoice and LaiOn, drawing out the primordial resonance of the island nation to its fullest. A comprehensive expression where voice, drums, and flutes unite.',
      videoUrl: 'https://youtu.be/f65mIotLbYM?si=wLJsby-9sxiE-IkQ',
    },
    {
      id: 'ro',
      name: 'Ro [LaiOn 籟音]',
      description: 'Fusion of festival energy and artistry with drums and flutes (3-6 members)',
      members: 6,
      image: 'https://public.readdy.ai/ai/img_res/edited_c021d6f9841df2c7d181d9033241d064_310c2559.jpg',
      details: 'Balancing the energy of festivals with refined artistry. A dynamic ensemble where the power of drums and the delicacy of flutes interweave.',
      videoUrl: 'https://youtu.be/AIW3BQ1NQ8s?si=BWqEVyfUvKGBG5kN',
    },
    {
      id: 'i',
      name: 'I [EarthVoice]',
      description: 'The rustic yet rich resonance of maritime people (2 members)',
      members: 2,
      image: 'https://readdy.ai/api/search-image?query=abstract-minimalist-representation-of-ocean-waves-and-earth-tones-deep-blue-water-meeting-sandy-shores-natural-organic-textures-serene-maritime-atmosphere-simple-clean-background-emphasizing-primal-connection-between-sea-and-land-artistic-photography-with-soft-lighting&width=600&height=800&seq=ensemble-i-001&orientation=portrait',
      details: 'Expressing the primordial resonance of sea and earth through voice and body. A minimal ensemble that is rustic yet possesses deep richness.',
      videoUrl: 'https://youtu.be/a_X4_Br1YSw?si=IJSglB3lf9F9i5c4',
    },
  ];

  return (
    <section
      id="ensemble"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`text-center mb-12 sm:mb-16 md:mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-sumi mb-3 sm:mb-4 tracking-wide">
            Ensemble "I～Ni"
          </h2>
          <p className="font-sans text-base sm:text-xl text-sumi/70 font-light">
            The resonance of the island nation, in four formations.
          </p>
        </div>

        {/* Ensemble Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {ensembles.map((ensemble, index) => (
            <EnsembleCard
              key={ensemble.id}
              ensemble={ensemble}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
}